import { getCache, putCache } from './native/nativeAdCache';
import { waitForPrebidIfRequired } from './prebid';
import { track } from '../tracking';
import { withAdRequestStartedEvent } from '../tracking/events';
import { checkIfEmptyAd } from '../adSpot/adSpotUtil';
import FetchError from '../utils/customErrorType';
var contentTypes = {
  JSON: 'application/json',
  HTML: 'text/html',
  HTML_UTF_8: 'text/html; charset=utf-8'
};
export var adTypes = {
  NATIVE: 'native',
  HTML: 'html',
  PREBID: 'prebid',
  NO_AD: 'no-ad'
};

var racePromiseWithTimeout = function racePromiseWithTimeout(adPromise, adSpot) {
  if (adSpot.nativeTimeout) {
    var maxTimeout = new Promise(function (_, reject) {
      setTimeout(function () {
        reject(new Error('Do not receive ad server response within timeout. Aborting native ad render!'));
      }, adSpot.nativeTimeout);
    });
    return Promise.race([maxTimeout, adPromise]);
  }

  return adPromise;
};

var cacheContent = function cacheContent(nativeAdCache, cacheKey) {
  return function (advertorialResponse) {
    if (cacheKey) {
      nativeAdCache.putCache(cacheKey, advertorialResponse);
    }

    return advertorialResponse;
  };
};

var isTrustedHtmlTagInContent = function isTrustedHtmlTagInContent(adSpot, adContent) {
  var viewId = adSpot.viewId,
      random = adSpot.random;
  var isTrustedHtmlRegEx = /<!-- is-trusted-html (\d+) -->/; // only get the first match to protect against ads brute forcing a match

  var firstMatch = adContent.match(isTrustedHtmlRegEx);
  return firstMatch && firstMatch[1] === "".concat(viewId).concat(random);
};

var getHtmlAdResponse = function getHtmlAdResponse(contentType, adSpot) {
  return function (adContent) {
    var isEmptyAd = checkIfEmptyAd('html', adContent);

    if (isEmptyAd) {
      return {
        contentType: contentType,
        adType: adTypes.NO_AD
      };
    }

    var isTrustedHtml = isTrustedHtmlTagInContent(adSpot, adContent);
    return {
      contentType: contentType,
      adType: adTypes.HTML,
      content: adContent,
      isTrustedHtml: isTrustedHtml
    };
  };
};

var getJsonAdResponse = function getJsonAdResponse(contentType, adSpot, nativeAdCache) {
  return function (content) {
    if (content.adtype === 'prebid') {
      return {
        contentType: contentType,
        adType: adTypes.PREBID,
        content: content
      };
    }

    return cacheContent(nativeAdCache, adSpot.cacheKey)({
      contentType: contentType,
      adType: adTypes.NATIVE,
      content: content
    });
  };
};

var handleResponse = function handleResponse(adSpot, nativeAdCache) {
  return function (response) {
    if (!response.ok) {
      return Promise.reject(new Error('Response code is not 200'));
    }

    var contentType = response.headers.get('Content-Type').toLowerCase();

    switch (contentType) {
      case contentTypes.JSON:
        return response.json().then(getJsonAdResponse(contentType, adSpot, nativeAdCache));

      case contentTypes.HTML:
      case contentTypes.HTML_UTF_8:
        return response.text().then(getHtmlAdResponse(contentType, adSpot));

      default:
        return Promise.reject(new Error("Response type not expected: ".concat(contentType)));
    }
  };
};

var requestAd = function requestAd(adSpot, shouldTrackEvent) {
  var nativeAdCache = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    getCache: getCache,
    putCache: putCache
  };
  var cachedResponse = adSpot.cacheKey && nativeAdCache.getCache(adSpot.cacheKey);

  if (cachedResponse) {
    return Promise.resolve(cachedResponse);
  }

  var adRequest = waitForPrebidIfRequired(adSpot).then(function (_ref) {
    var bidResult = _ref.bidResult,
        isDemoApp = _ref.isDemoApp;
    track(withAdRequestStartedEvent({
      bidResult: bidResult
    }), adSpot, shouldTrackEvent);
    adSpot.isDemoApp = isDemoApp;
    return fetch(adSpot.getLink(bidResult), {
      credentials: 'include'
    })["catch"](function () {
      throw new FetchError('Fetch network error');
    });
  });
  return racePromiseWithTimeout(adRequest, adSpot).then(handleResponse(adSpot, nativeAdCache));
};

export default requestAd;
//# sourceMappingURL=requestAd.js.map